/**
 * feedStore — the ONE channel-owned app store (sweep contract §4; inbox-is-work
 * wave).
 *
 * The rail tab strip, the inbox panel, the tab item lists, the WORK records,
 * and the toasts all render from THIS store — component-local mirrors are dead.
 *
 * Laws:
 * - Queries are DRAINS that reconcile the store: one `query {tab:"feed"}` per
 *   page lifetime (single-flight, shared by rail + bell), and per-tab serial
 *   drains 1..total against the served `sync.totals` (§1 — the client owns
 *   the drain; the outlet serves one piece per scope per ask). WS folds keep
 *   the store live thereafter; long-gap recovery is the app-level reload
 *   (useAuth online/visibility), which reconciles fresh by construction.
 * - foldLiveAnnouncement is THE lane classifier (the ONE client crossing):
 *   the mirrored ref sets decide — feed-row / inbox-row refreshes fold the
 *   served bodies; notify/sync/status frames fold as keyed inbox entries
 *   (SUPERSESSION by ref — one entry per work unit, never per transition).
 *   Toasts are emitted ONLY from this crossing (frames + finished work), only
 *   when the user is NOT on the originating surface; bursts COLLAPSE into one
 *   stacked "N updates" toast. Never a refetch.
 * - EVERYTHING IS WORK: every ChannelClient.call mints a local work record
 *   here (start → complete | fail:* | retry — the existing machine enum),
 *   rendered optimistically IMMEDIATELY. Local records fold into the inbox
 *   view (kind "status") local-first; the arrangement reconcile trues them up
 *   against the world-authored list (terminal locals yield to world truth).
 * - Writes fold locally (the world claims are keyed/idempotent); the next
 *   generation's reconcile trues them up — no post-write refetch.
 * - The store dumps into the ONE session snapshot (newest-first, byte-bounded
 *   — a CACHE of served pieces, never presented as complete: hydrated slices
 *   boot `loaded:false` so the drain always reconciles) and boot renders from
 *   that snapshot instantly (§29). Work records and toasts never persist.
 */
import { useSyncExternalStore } from "react";

import { readSessionSnapshot, replaceSessionSlot } from "../services/sessionSnapshot";
import type {
  ConversationFeedItem,
  ConversationQueryResult,
  ConversationQueryTab,
  FeedPrioritizedEntry,
  FeedState,
  FeedTabFace,
  InboxEntry,
  InboxEntryKind,
  InboxState,
} from "./client";
import type { Announcement } from "./contract";
import {
  classifyFeedFrame,
  evidenceDataOf,
  inboxEntryOfAnnouncement,
  statusWordOf,
  type WorkStatus,
} from "./feedWave";

type FeedQuery = (input: {
  tab: ConversationQueryTab;
  limit?: number;
  serial?: number;
}) => Promise<ConversationQueryResult>;

export type FeedTabSlice = {
  items: ConversationFeedItem[];
  /** drained to the served totals this page lifetime (ws frames keep it live) */
  loaded: boolean;
  loading: boolean;
  error: string;
  sync: { serial: number; totals: Record<string, number> } | null;
};

/** ONE local work unit — every ChannelClient.call. Status is the EXISTING
 * machine enum; `seen` is local-only bookkeeping (a local record has no world
 * row edge yet, so it never rides the world seen-write lane). */
export type WorkRecord = {
  id: string;
  label: string;
  status: WorkStatus;
  conversationId?: string;
  timestamp: string;
  seen?: boolean;
};

export type FeedToast = {
  id: string;
  label: string;
  conversationId: string;
  /** collapsed burst size — label reads "N updates" when > 1 */
  count: number;
  at: number;
};

export type FeedStoreState = {
  feed: FeedState | null;
  inbox: InboxState | null;
  work: WorkRecord[];
  arrangementLoaded: boolean;
  arrangementLoading: boolean;
  arrangementError: string;
  byTab: Record<string, FeedTabSlice>;
  toasts: FeedToast[];
};

export const EMPTY_TAB_SLICE: FeedTabSlice = {
  items: [], loaded: false, loading: false, error: "", sync: null,
};

const EMPTY_INBOX: InboxState = { kind: [], open: false, list: [], seen: [] };

// the tab drain page size — the outlet's own limit clamp ceiling.
const TAB_ASK_LIMIT = 60;
// the persisted feed dump budget (chars) — the dump is a CACHE of served
// pieces bounded newest-first; the drain completes it (never a silent cap
// presented as complete data — hydrated slices always reconcile).
const DUMP_BUDGET_CHARS = 300_000;
const MAX_TOASTS = 4;
// toasts landing together coalesce into ONE stacked "N updates" toast.
const TOAST_BURST_MS = 5_000;

type FeedDump = {
  feed: FeedState | null;
  inbox: InboxState | null;
  byTab: Record<string, { items: ConversationFeedItem[] }>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// ── the served body projections (the new body IS the only shape — a legacy
//    spelling fails LOUD as an arrangement error, never a silent empty) ──────
/** One served tab FACE (the outlet's resolved tab row beside the token
 * order): id + bare icon word + humanized label/description — rendered
 * AS-IS by the rail (the tooltip/hover truth; the client derives nothing). */
const tabFaceOf = (raw: unknown): FeedTabFace | null => {
  if (!isRecord(raw)) return null;
  const id = String(raw.id || "");
  if (!id) return null;
  return {
    id,
    icon: String(raw.icon ?? ""),
    label: String(raw.label ?? ""),
    description: String(raw.description ?? ""),
  };
};

const prioritizedEntryOf = (raw: unknown): FeedPrioritizedEntry | null => {
  if (!isRecord(raw)) return null;
  const ref = Number(raw.ref);
  const artifact = String(raw.artifact ?? "");
  if (!Number.isFinite(ref) || !artifact) return null;
  return {
    ref,
    artifact,
    label: Array.isArray(raw.label) ? raw.label.map(String).filter(Boolean) : [String(raw.label ?? "")].filter(Boolean),
    timestamp: String(raw.timestamp ?? ""),
    ...(typeof raw.conversation === "string" && raw.conversation ? { conversation: raw.conversation } : {}),
  };
};

/** The served left-overlay word (the confirm-loop wave: "" | "inbox" | "tab"
 * — one word by construction). A persisted pre-wave BOOLEAN read-tolerates:
 * true folds to the tab strip (the old rail's visible content). */
const leftOverlayOf = (value: unknown): FeedState["left"] => {
  if (value === "inbox" || value === "tab") return value;
  if (value === true) return "tab";
  return "";
};

const feedStateOf = (raw: unknown): FeedState | null => {
  if (!isRecord(raw) || !Array.isArray(raw.tab)) return null;
  const icon = isRecord(raw.icon) ? raw.icon : {};
  return {
    ...(raw.machine !== undefined ? { machine: raw.machine } : {}),
    tab: raw.tab.map(String).filter(Boolean),
    mode: Array.isArray(raw.mode) ? raw.mode.map(String).filter(Boolean) : [],
    current: String(raw.current || ""),
    left: leftOverlayOf(raw.left),
    right: raw.right === true,
    middle: String(raw.middle || ""),
    prioritized: Array.isArray(raw.prioritized)
      ? raw.prioritized.map(prioritizedEntryOf).filter((e): e is FeedPrioritizedEntry => !!e)
      : [],
    icon: {
      mode: String(icon.mode ?? ""),
      tab: String(icon.tab ?? ""),
      inbox: String(icon.inbox ?? ""),
      prioritized: String(icon.prioritized ?? ""),
    },
    tabs: Array.isArray(raw.tabs)
      ? raw.tabs.map(tabFaceOf).filter((face): face is FeedTabFace => !!face)
      : [],
  };
};

const inboxEntryOfRaw = (raw: unknown): InboxEntry | null => {
  if (!isRecord(raw)) return null;
  const ref = Number(raw.ref);
  const kind: InboxEntryKind | null =
    raw.kind === "notify" || raw.kind === "status" ? raw.kind : null;
  if (!Number.isFinite(ref) || !kind) return null;
  return {
    ref,
    kind,
    icon: String(raw.icon ?? ""),
    label: Array.isArray(raw.label) ? raw.label.map(String).filter(Boolean) : [String(raw.label ?? "")].filter(Boolean),
    timestamp: String(raw.timestamp ?? ""),
    ...(typeof raw.conversation === "string" && raw.conversation ? { conversation: raw.conversation } : {}),
  };
};

const inboxStateOf = (raw: unknown): InboxState | null => {
  if (!isRecord(raw) || !Array.isArray(raw.list)) return null;
  return {
    ...(raw.machine !== undefined ? { machine: raw.machine } : {}),
    kind: Array.isArray(raw.kind)
      ? raw.kind.filter((k): k is InboxEntryKind => k === "notify" || k === "status")
      : [],
    open: raw.open === true,
    list: raw.list.map(inboxEntryOfRaw).filter((entry): entry is InboxEntry => !!entry),
    seen: Array.isArray(raw.seen)
      ? raw.seen.filter(isRecord).map((pair) => ({ ref: pair.ref, timestamp: String(pair.timestamp ?? "") }))
      : [],
  };
};

class FeedStore {
  private listeners = new Set<() => void>();
  private state: FeedStoreState;
  private arrangementInFlight: Promise<void> | null = null;
  private tabInFlight = new Map<string, Promise<void>>();
  /** the active conversation — the fold's "is the user watching" input
   * (kept current by useChannel; never rendered from). */
  private activeConversationId = "";
  /** announcement ids already toasted — a frame never toasts twice. */
  private toastedIds = new Set<string>();
  private toastSerial = 0;
  /** THE STANDING RAIL DECLARATION (Ori 2026-08-04) — a rail click DECLARES an
   * absolute open value, it never toggles a value that may still be in
   * flight. The declaration is authoritative from the click until the world
   * SERVES it back; only then is it acknowledged and dropped. Without this the
   * rail read its own next value off a projection three different serves
   * replace wholesale (`ensureArrangement`, the ws feed-row fold, the boot
   * snapshot), so a click landing before its write came back was computed
   * against the PRE-click value and inverted: the click that meant open closed
   * the rail. Holding the declaration makes the race constructive — a serve
   * that disagrees is simply not caught up yet, and the declared value stands.
   * `null` = nothing declared; the serve is the truth. */
  private leftDeclared: FeedState["left"] | null = null;
  private rightDeclared: boolean | null = null;

  /** Overlay the standing declarations onto a SERVED feed row, and retire a
   * declaration the serve has caught up with (acknowledged). Every path that
   * replaces `feed` from a served body goes through here. */
  private withRailDeclaration(feed: FeedState | null): FeedState | null {
    if (!feed) return feed;
    if (this.leftDeclared !== null) {
      if (feed.left === this.leftDeclared) this.leftDeclared = null;
      else feed = { ...feed, left: this.leftDeclared };
    }
    if (this.rightDeclared !== null) {
      if (feed.right === this.rightDeclared) this.rightDeclared = null;
      else feed = { ...feed, right: this.rightDeclared };
    }
    return feed;
  }

  /** THE RAIL CLICK — resolve the absolute next value from the store's LIVE
   * projection (declaration over serve), never from a React render snapshot
   * that can be a frame behind. Returns the value the caller announces, so the
   * write and the fold always carry the SAME declared boolean. */
  declareRail(lane: "inbox" | "tab" | "right"): boolean {
    const feed = this.state.feed;
    const next = lane === "right"
      ? !(feed?.right === true)
      : (feed?.left ?? "") !== lane;
    this.applyFeedRail(lane, next);
    return next;
  }

  constructor() {
    this.state = this.hydrateFromSnapshot();
  }

  /** Boot state — the session snapshot dump renders INSTANTLY; every slice
   * hydrates `loaded:false` so the first ensure* reconciles (the dump is a
   * cache, the drain completes it). */
  private hydrateFromSnapshot(): FeedStoreState {
    const base: FeedStoreState = {
      feed: null,
      inbox: null,
      work: [],
      arrangementLoaded: false,
      arrangementLoading: false,
      arrangementError: "",
      byTab: {},
      toasts: [],
    };
    const dump = readSessionSnapshot().feed;
    if (!isRecord(dump)) return base;
    const d = dump as Partial<FeedDump>;
    const byTab: Record<string, FeedTabSlice> = {};
    if (isRecord(d.byTab)) {
      for (const [tab, slice] of Object.entries(d.byTab)) {
        const items = isRecord(slice) && Array.isArray((slice as { items?: unknown }).items)
          ? (slice as { items: ConversationFeedItem[] }).items
          : [];
        byTab[tab] = { ...EMPTY_TAB_SLICE, items };
      }
    }
    return {
      ...base,
      feed: feedStateOf(d.feed),
      inbox: inboxStateOf(d.inbox),
      byTab,
    };
  }

  getState = (): FeedStoreState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private set(patch: Partial<FeedStoreState>): void {
    this.state = { ...this.state, ...patch };
    this.persist();
    for (const listener of this.listeners) listener();
  }

  private setTab(tab: string, patch: Partial<FeedTabSlice>): void {
    const current = this.state.byTab[tab] || EMPTY_TAB_SLICE;
    this.set({ byTab: { ...this.state.byTab, [tab]: { ...current, ...patch } } });
  }

  setActiveConversation(id: string): void {
    this.activeConversationId = String(id || "");
  }

  /** The atomic session dump — newest-first per slice, byte-bounded by
   * halving item counts until the serialized dump fits the budget. Work
   * records, toasts, and transient loading/error state never persist. */
  private persist(): void {
    let keep = Number.POSITIVE_INFINITY;
    for (;;) {
      const dump: FeedDump = {
        feed: this.state.feed,
        inbox: this.state.inbox
          ? { ...this.state.inbox, list: this.state.inbox.list.slice(0, Math.min(keep, this.state.inbox.list.length)) }
          : null,
        byTab: Object.fromEntries(
          Object.entries(this.state.byTab).map(([tab, slice]) => [
            tab, { items: slice.items.slice(0, Math.min(keep, slice.items.length)) },
          ]),
        ),
      };
      const size = JSON.stringify(dump).length;
      if (size <= DUMP_BUDGET_CHARS || keep === 0) {
        replaceSessionSlot("feed", dump);
        return;
      }
      const largest = Math.max(
        this.state.inbox?.list.length ?? 0,
        ...Object.values(this.state.byTab).map((slice) => slice.items.length),
        1,
      );
      keep = Math.floor(Math.min(keep, largest) / 2);
    }
  }

  // ── the arrangement + inbox reconcile (ONE query{tab:"feed"} per page
  //    lifetime, single-flight — the rail, the bell, and the chrome SHARE it;
  //    ws folds keep it live thereafter, and the app-level recovery paths
  //    (useAuth's online/visibility reload) own the real long-gap refresh) ────
  ensureArrangement(query: FeedQuery): Promise<void> {
    if (this.state.arrangementLoaded) return Promise.resolve();
    if (this.arrangementInFlight) return this.arrangementInFlight;
    this.set({ arrangementLoading: true, arrangementError: "" });
    this.arrangementInFlight = query({ tab: "feed", limit: 18 })
      .then((res) => {
        const feed = feedStateOf(res.feed);
        const inbox = inboxStateOf(res.inbox);
        // the new body IS the only shape — a served row that misses it is a
        // LOUD contract error, never a silently empty rail (§29).
        if (res.feed !== undefined && !feed) {
          throw new Error("feed row shape mismatch — expected the served {tab,mode,current,left,right,prioritized,icon} body");
        }
        if (res.inbox !== undefined && !inbox) {
          throw new Error("inbox row shape mismatch — expected the served {kind,open,list,seen} body");
        }
        this.set({
          // a rail click that raced this drain KEEPS its declared value — the
          // serve is simply not caught up yet (it acknowledges on the next one).
          feed: this.withRailDeclaration(feed),
          inbox,
          // the world list is truth for finished work — terminal local records
          // yield to the reconcile; in-flight ones stay (local-first).
          work: this.state.work.filter((record) => record.status === "start" || record.status === "retry"),
          arrangementLoaded: true,
          arrangementLoading: false,
          arrangementError: "",
        });
      })
      .catch((error) => {
        // §29: the error renders LOUD; the stale snapshot state stays rendered.
        this.set({
          arrangementLoading: false,
          arrangementError: error instanceof Error ? error.message : String(error),
        });
      })
      .finally(() => { this.arrangementInFlight = null; });
    return this.arrangementInFlight;
  }

  // ── the tab drain (§1: serial 1..total per the served sync.totals) ───────
  ensureTab(tab: string, query: FeedQuery): Promise<void> {
    const slice = this.state.byTab[tab] || EMPTY_TAB_SLICE;
    const held = this.tabInFlight.get(tab);
    if (held) return held;
    if (slice.loaded) return Promise.resolve();
    this.setTab(tab, { loading: true, error: "" });
    const request = (async () => {
      try {
        const first = await query({ tab: tab as ConversationQueryTab, limit: TAB_ASK_LIMIT, serial: 1 });
        const seen = new Set<string>();
        const drained: ConversationFeedItem[] = [];
        const fold = (items: ConversationFeedItem[] | undefined) => {
          for (const item of Array.isArray(items) ? items : []) {
            const key = String(item.id || item.announcementId || "");
            if (!key || seen.has(key)) continue;
            seen.add(key);
            drained.push(item);
          }
        };
        fold(first.items);
        const sync = first.sync && typeof first.sync === "object"
          ? { serial: Number(first.sync.serial || 1), totals: first.sync.totals || {} }
          : null;
        // the first piece replaces the snapshot-stale slice immediately.
        this.setTab(tab, { items: [...drained], sync });
        // the DRAIN: serial 2..max(total) — one ask per piece; the outlet asks
        // every scope leg at that serial (sparse pieces tolerated). Order:
        // the first piece keeps its served order (the outlet leads with the
        // prioritized cards); later pieces append deduped — the world's serials
        // are write-assigned, so later pieces are older rows.
        const maxTotal = sync ? Math.max(0, ...Object.values(sync.totals).map(Number)) : 0;
        for (let serial = 2; serial <= maxTotal; serial += 1) {
          const piece = await query({ tab: tab as ConversationQueryTab, limit: TAB_ASK_LIMIT, serial });
          fold(piece.items);
          this.setTab(tab, { items: [...drained] });
        }
        this.setTab(tab, { loaded: true, loading: false, error: "" });
      } catch (error) {
        // §29: loud error state, never eternal Loading; stale items stay.
        this.setTab(tab, {
          loading: false,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    })().finally(() => {
      this.tabInFlight.delete(tab);
    });
    this.tabInFlight.set(tab, request);
    return request;
  }

  /** Force a post-write reconcile. If switching tabs already started the
   * ordinary first drain, wait for it and then run one fresh drain so an
   * accepted template fork cannot be hidden by that earlier read. */
  async refreshTab(tab: string, query: FeedQuery): Promise<void> {
    const held = this.tabInFlight.get(tab);
    if (held) await held;
    this.setTab(tab, { loaded: false });
    await this.ensureTab(tab, query);
  }

  /** Optimistic lifecycle fold. The accepted write is still reconciled by a
   * fresh drain; on failure the caller refreshes and the world restores the
   * card. This also removes it from the persisted session snapshot now. */
  removeTabItem(tab: string, id: string): void {
    const slice = this.state.byTab[tab] || EMPTY_TAB_SLICE;
    this.setTab(tab, {
      items: slice.items.filter((item) => item.id !== id),
    });
  }

  /** Put back the exact optimistic item when the lifecycle write refuses.
   * The next successful drain remains authoritative for order and presence. */
  restoreTabItem(tab: string, item: ConversationFeedItem, index: number): void {
    const slice = this.state.byTab[tab] || EMPTY_TAB_SLICE;
    if (slice.items.some((candidate) => candidate.id === item.id)) return;
    const at = Math.max(0, Math.min(index, slice.items.length));
    const items = [...slice.items];
    items.splice(at, 0, item);
    this.setTab(tab, { items });
  }

  // ── the toast layer (emitted ONLY from this store's crossings) ───────────
  /** True when the user is NOT watching the given surface — moved to another
   * conversation, or the tab is hidden. */
  private notWatching(conversationId: string): boolean {
    const hidden = typeof document !== "undefined" && document.hidden;
    return hidden || !conversationId || conversationId !== this.activeConversationId;
  }

  /** Push a toast with burst COLLAPSE: several toasts landing together (same
   * conversation, or within the burst window) coalesce into one stacked
   * "N updates" toast. */
  private nextToasts(label: string, conversationId: string): FeedToast[] {
    return this.nextToastsFrom(this.state.toasts, label, conversationId);
  }

  private nextToastsFrom(toasts: FeedToast[], label: string, conversationId: string): FeedToast[] {
    const now = Date.now();
    const last = toasts[toasts.length - 1];
    if (last && (now - last.at < TOAST_BURST_MS || (!!conversationId && last.conversationId === conversationId))) {
      const count = last.count + 1;
      return [...toasts.slice(0, -1), {
        ...last,
        count,
        label: `${count} updates`,
        conversationId: last.conversationId === conversationId ? conversationId : "",
        at: now,
      }];
    }
    this.toastSerial += 1;
    return [...toasts, {
      id: `toast-${now.toString(36)}-${this.toastSerial}`,
      label,
      conversationId,
      count: 1,
      at: now,
    }].slice(-MAX_TOASTS);
  }

  // ── THE ONE CLIENT CROSSING: live ws frames FOLD (zero refetch) ──────────
  /** THE lane classifier fold: a live ws announcement folds into the store —
   * feed-row / inbox-row refresh frames fold the served bodies; notify /
   * sync / status frames fold as keyed inbox entries (supersession by ref).
   * Toasts fire ONLY here (and from finished work), only when the user is
   * NOT on the originating surface. Never a refetch. */
  foldLiveAnnouncement(a: Announcement): void {
    const lane = classifyFeedFrame(a);
    if (!lane) return;
    if (lane === "feed-row") {
      const feed = feedStateOf(evidenceDataOf(a));
      // a ws feed-row refresh carries the WORLD row body (token order, mode,
      // rail binaries, prioritized entries, button icons) — the resolved tab FACES are the query serve's
      // outlet addition, so a face-less refresh preserves the held faces.
      if (feed) {
        this.set({
          feed: this.withRailDeclaration(
            feed.tabs.length ? feed : { ...feed, tabs: this.state.feed?.tabs ?? [] }),
        });
      }
      return;
    }
    if (lane === "inbox-row") {
      const inbox = inboxStateOf(evidenceDataOf(a));
      if (!inbox) return;
      // THE NOTIFY TOAST (the confirm-loop wave, Ori 2026-07-25): a
      // first-class notify ROW never rides the ws itself — its arrival IS
      // the refreshed inbox row. A NEW unseen NOTIFY entry in the refreshed
      // list toasts WITH ITS MESSAGE (the humanized labels); sync/status
      // arrivals accrete silently (they are in the list but never counted
      // and never toast from here).
      const heldRefs = new Set((this.state.inbox?.list ?? []).map((entry) => entry.ref));
      const seenRefs = new Set(inbox.seen.map((pair) => String(pair?.ref ?? "").trim()));
      const fresh = inbox.list.filter((entry) =>
        entry.kind === "notify" && !heldRefs.has(entry.ref) && !seenRefs.has(String(entry.ref)));
      let toasts = this.state.toasts;
      for (const entry of fresh) {
        const toastId = `notify-${entry.ref}`;
        if (this.toastedIds.has(toastId)) continue;
        this.toastedIds.add(toastId);
        const message = entry.label.filter(Boolean).join(" — ") || "Notification";
        toasts = this.nextToastsFrom(toasts, message, entry.conversation || "");
      }
      this.set({ inbox, ...(toasts !== this.state.toasts ? { toasts } : {}) });
      return;
    }
    const entry = inboxEntryOfAnnouncement(a, lane);
    if (!entry) return;
    const inbox = this.state.inbox ?? EMPTY_INBOX;
    // keyed SUPERSESSION: a status change replaces the same-ref entry in
    // place (one entry per work unit); a new ref lands newest-first.
    const index = inbox.list.findIndex((held) => held.ref === entry.ref);
    const list = index >= 0
      ? [...inbox.list.slice(0, index), entry, ...inbox.list.slice(index + 1)]
      : [entry, ...inbox.list];
    const toastWorthy = a.sender !== "human"
      && this.notWatching(a.conversationId || "")
      && !this.toastedIds.has(a.id);
    if (toastWorthy) this.toastedIds.add(a.id);
    this.set({
      inbox: { ...inbox, list },
      ...(toastWorthy
        ? { toasts: this.nextToasts(entry.label[0] || "Update", a.conversationId || "") }
        : {}),
    });
  }

  dismissToast(id: string): void {
    if (!this.state.toasts.some((toast) => toast.id === id)) return;
    this.set({ toasts: this.state.toasts.filter((toast) => toast.id !== id) });
  }

  dismissOldestToast(): void {
    if (!this.state.toasts.length) return;
    this.set({ toasts: this.state.toasts.slice(1) });
  }

  // ── EVERYTHING IS WORK: the local work records (ChannelClient.call) ──────
  /** Mint a work unit — rendered optimistically IMMEDIATELY (deliver first,
   * never wait); it folds into the inbox view as a kind:"status" entry. */
  beginWork(record: Omit<WorkRecord, "timestamp">): void {
    this.set({
      work: [{ ...record, timestamp: new Date().toISOString().replace(/Z$/, "") }, ...this.state.work],
    });
  }

  /** Walk a work unit's status (the existing enum). A TERMINAL status for a
   * surface the user is not watching ALWAYS toasts and stays in the inbox. */
  setWorkStatus(id: string, status: WorkStatus): void {
    const index = this.state.work.findIndex((record) => record.id === id);
    if (index < 0) return;
    const record = { ...this.state.work[index], status };
    const work = [...this.state.work.slice(0, index), record, ...this.state.work.slice(index + 1)];
    const terminal = status === "complete" || status.startsWith("fail:");
    const hidden = typeof document !== "undefined" && document.hidden;
    // a background/bookkeeping call (no conversation) completing while the
    // user is present is not an away-surface event — only work for ANOTHER
    // conversation, or any finished work while the tab is hidden, toasts.
    const toastWorthy = terminal
      && ((!!record.conversationId && record.conversationId !== this.activeConversationId) || hidden);
    this.set({
      work,
      ...(toastWorthy
        ? { toasts: this.nextToasts(`${record.label} ${statusWordOf(status)}`, record.conversationId || "") }
        : {}),
    });
  }

  /** Local seen bookkeeping for work records (they have no world row edge, so
   * they never ride the world seen-write lane). */
  markLocalWorkSeen(): void {
    if (!this.state.work.some((record) => !record.seen)) return;
    this.set({ work: this.state.work.map((record) => (record.seen ? record : { ...record, seen: true })) });
  }

  // ── local write folds (the world claims are keyed; reconcile trues up) ───
  applyTabOrder(order: string[]): void {
    const feed = this.state.feed;
    if (!feed) return;
    const held = new Set(feed.tab);
    const next = order.filter((token) => held.has(token));
    for (const token of feed.tab) if (!next.includes(token)) next.push(token);
    this.set({ feed: { ...feed, tab: next } });
  }

  /** A rail lane's open fold (the confirm-loop wave): the LEFT side is ONE
   * overlay word — opening "inbox" or "tab" claims it (and implicitly closes
   * the other); closing clears the rail only from the overlay that is
   * showing. The RIGHT side keeps its own binary
   * (sci//feed:right:@binary). Pre-serve boot folds onto an empty local
   * projection; the arrangement reconcile trues it up. */
  applyFeedRail(lane: "inbox" | "tab" | "right", open: boolean): void {
    const feed = this.state.feed
      ?? {
        tab: [], mode: [], current: "", left: "" as FeedState["left"], right: false,
        middle: "", prioritized: [],
        icon: { mode: "", tab: "", inbox: "", prioritized: "" }, tabs: [],
      };
    if (lane === "right") {
      // the DECLARATION stands even when the fold is a no-op: the local
      // projection may already agree while the world has not been told yet,
      // and a serve that still carries the old value must not undo the click.
      this.rightDeclared = open;
      if (feed.right === open) return;
      this.set({ feed: { ...feed, right: open } });
      return;
    }
    const left: FeedState["left"] = open ? lane : (feed.left === lane ? "" : feed.left);
    this.leftDeclared = left;
    if (feed.left === left) return;
    this.set({ feed: { ...feed, left } });
  }

  /** The mode selection fold (sci//feed:@(ui:kind:mode):current). */
  applyModeCurrent(current: string): void {
    const feed = this.state.feed;
    if (!feed || feed.current === current) return;
    this.set({ feed: { ...feed, current } });
  }

  /** The middle current-tab fold (sci//feed:middle:@(tab:current)). */
  applyFeedMiddle(middle: string): void {
    const feed = this.state.feed;
    if (!feed || feed.middle === middle) return;
    this.set({ feed: { ...feed, middle } });
  }

  applyInboxSeen(refs: string[], timestamp: string): void {
    if (!refs.length) return;
    const inbox = this.state.inbox ?? EMPTY_INBOX;
    const held = new Set(inbox.seen.map((entry) => String(entry?.ref ?? "").trim()));
    const additions = refs
      .map((ref) => ref.trim())
      .filter((ref) => ref && !held.has(ref))
      .map((ref) => ({ ref, timestamp }));
    if (!additions.length) return;
    this.set({ inbox: { ...inbox, seen: [...inbox.seen, ...additions] } });
  }

  /** Optimistic item fold for a write whose result lands in a tab slice
   * (e.g. the template fork) — the next generation's reconcile trues it up. */
  foldOptimisticItem(tab: string, item: ConversationFeedItem): void {
    const slice = this.state.byTab[tab] || EMPTY_TAB_SLICE;
    this.setTab(tab, { items: [item, ...slice.items] });
  }
}

/** The ONE channel store instance. */
export const feedStore = new FeedStore();

/** React binding — the whole store state (replaced immutably on change). */
export const useFeedStoreState = (): FeedStoreState =>
  useSyncExternalStore(feedStore.subscribe, feedStore.getState);
